// F131 (Pavel 2026-08-11): "Kdyz kliknu na notifikaci, tak me to vzdycky da na
// nejakou mezistranku. (...) Da me to na mezistranku, ktera me smeruje na beta
// testovani - a pritom klikam na ,odkazy na objednavku jsou v tve prodejni
// strance', takze by me to melo dat do sekce prodejni stranka."
//
// This is the ONE registry that answers "where does this notification lead".
// Before it, four surfaces answered it separately and none of them owned the
// question: the queue wrote whatever URL the call site happened to build, the
// bell fell back to "/", the mail button fell back to "/", and the staff bell
// GUESSED with a regex over the title (`/q2|sales/i.test(...)`). Measured on
// dev D1 the day this shipped: 83 partner-review rows led to a bare client with
// no step, 23 client material rows and 285 step-unlocked rows led to "/", and
// 3280 action_required rows carried no destination at all.
//
// The rules:
//   1. A notification about a step leads to THAT step. Never to a recommendation
//      of a different step.
//   2. A type with no entry here is a gate failure, not a silent "/".
//   3. A type that legitimately has no step leads to the overview and says WHY
//      in `overviewReason`. The overview is a signpost with every step and its
//      state, so it is an honest landing; the "co delat ted" card is not.

/**
 * Every client surface a notification may lead to. This is the ONE list; the
 * rail (`CLIENT_OPEN_TARGETS`), the Worker inbox and the bell all read it, so a
 * surface cannot be linkable in one of them and stripped in another.
 */
export const CLIENT_NOTIFICATION_SURFACES = [
  "home", "onboarding_call", "marketing_call", "profile", "account",
  "app", "app:apple", "q1", "q2", "knowhow", "training", "beta", "beta:feedback",
  "sales", "strategy", "salespage", "emails", "webinar", "publish", "stripe",
  "payments", "handover", "app:questionnaire", "app:release"
] as const;

export type ClientNotificationSurface = (typeof CLIENT_NOTIFICATION_SURFACES)[number];

const CLIENT_SURFACE_SET: ReadonlySet<string> = new Set(CLIENT_NOTIFICATION_SURFACES);

export function isClientNotificationSurface(value: string): value is ClientNotificationSurface {
  return CLIENT_SURFACE_SET.has(value);
}

/**
 * The staff console opens a client ON a step (`?client=<id>&staffStep=<step>`).
 * These are the rail step keys that console understands as a workspace.
 */
export const STAFF_NOTIFICATION_STEPS = [
  "onboarding_call", "profile", "apple", "q1", "knowhow", "training", "beta",
  "feedback", "marketing_call", "q2", "strategy", "salespage", "emails",
  "webinar", "stripe", "payments", "app_questionnaire", "salespage_publish", "handover",
  "app_release"
] as const;

export type StaffNotificationStep = (typeof STAFF_NOTIFICATION_STEPS)[number];

const STAFF_STEP_SET: ReadonlySet<string> = new Set(STAFF_NOTIFICATION_STEPS);

export function isStaffNotificationStep(value: string): value is StaffNotificationStep {
  return STAFF_STEP_SET.has(value);
}

/**
 * What a notification's payload can tell us about WHICH thing it is about. The
 * resolver reads these; it never reads a title, a body or a command id, because
 * copy is not a destination (that regex is what this registry replaces).
 */
export interface NotificationFacts {
  /** `data.deliverableKind`, or the audit row's kind for a legacy material row. */
  readonly deliverableKind?: string | null;
  /** `data.overrideKind` - which client state the partner reopened or approved. */
  readonly overrideKind?: string | null;
  /** The section the client is really on now, when the payload named several. */
  readonly activeSection?: string | null;
  /** `payload.sections` / `data.sections`, in the order the payload wrote them. */
  readonly sections?: readonly string[] | null;
  /** `data.action.step` of a confirmation nudge (`apple` / `profile`). */
  readonly confirmStep?: string | null;
  /** Which call an occurrence-bound reminder belongs to. */
  readonly callKind?: string | null;
}

type Resolver = (facts: NotificationFacts) => string | null;

interface TargetRule {
  /** Where the CLIENT goes. Null when this type never reaches a client. */
  readonly client?: Resolver;
  /** Which step the STAFF console opens. Null when this type is client-only. */
  readonly staff?: Resolver;
  /**
   * Written reason this type may land on the overview instead of a step. A rule
   * that can return null without one is a mapping somebody forgot.
   */
  readonly overviewReason?: string;
  /** This type is never shown in a bell (sign-in links, invites). */
  readonly excludedFromInbox?: boolean;
}

// ---------------------------------------------------------------------------
// The four things a destination can be derived FROM.
// ---------------------------------------------------------------------------

/**
 * A material maps to its own step, on both sides. `audit`/`plan` are the two
 * legacy strategy gates and `avatar` has no rail step of its own (it lives in
 * attachments), so it lands on the sales overview / the profile step.
 */
const DELIVERABLE_CLIENT_SURFACE: Readonly<Record<string, ClientNotificationSurface>> = {
  audit: "strategy",
  plan: "strategy",
  strategy: "strategy",
  salespage: "salespage",
  emails: "emails",
  webinar: "webinar",
  avatar: "sales"
};

const DELIVERABLE_STAFF_STEP: Readonly<Record<string, StaffNotificationStep>> = {
  audit: "strategy",
  plan: "strategy",
  strategy: "strategy",
  salespage: "salespage",
  emails: "emails",
  webinar: "webinar",
  avatar: "profile"
};

/**
 * The client states the partner can reopen or approve (F104) map to the surface
 * the client has to work on next.
 */
const OVERRIDE_CLIENT_SURFACE: Readonly<Record<string, ClientNotificationSurface>> = {
  profile: "profile",
  apple: "app:apple",
  q1_submit: "q1",
  q2_submit: "q2",
  knowhow_submit: "knowhow",
  video_onboarding: "onboarding_call",
  video_marketing: "marketing_call",
  strategy: "strategy",
  salespage: "salespage",
  emails: "emails",
  webinar: "webinar"
};

/** A section key is a client surface in every case the product queues. */
function sectionSurface(facts: NotificationFacts): string | null {
  const named = facts.activeSection
    ?? (facts.sections ?? []).find((value) => typeof value === "string" && isClientNotificationSurface(value))
    ?? null;
  return named && isClientNotificationSurface(named) ? named : null;
}

/** The staff console names the beta section's two steps separately. */
const SECTION_STAFF_STEP: Readonly<Record<string, StaffNotificationStep>> = {
  onboarding_call: "onboarding_call",
  marketing_call: "marketing_call",
  q1: "q1",
  q2: "q2",
  knowhow: "knowhow",
  training: "training",
  beta: "beta",
  sales: "strategy",
  stripe: "stripe",
  handover: "handover",
  app: "app_questionnaire"
};

function sectionStaffStep(facts: NotificationFacts): string | null {
  const named = facts.activeSection
    ?? (facts.sections ?? []).find((value) => typeof value === "string" && value in SECTION_STAFF_STEP)
    ?? null;
  return named ? SECTION_STAFF_STEP[named] ?? null : null;
}

function deliverableClientSurface(facts: NotificationFacts): string | null {
  const kind = facts.deliverableKind;
  return kind ? DELIVERABLE_CLIENT_SURFACE[kind] ?? null : null;
}

function deliverableStaffStep(facts: NotificationFacts): string | null {
  const kind = facts.deliverableKind;
  return kind ? DELIVERABLE_STAFF_STEP[kind] ?? null : null;
}

const fixed = (surface: ClientNotificationSurface): Resolver => () => surface;
const fixedStep = (step: StaffNotificationStep): Resolver => () => step;

// Declared ABOVE the registry: `NOTIFICATION_TARGETS` is initialised the moment
// this module loads, so a `const` it reads must already exist. A `function` is
// hoisted and would survive below; a `const` in the temporal dead zone throws
// on import and takes every notification down with it.
const CALL_REASON =
  "A reminder whose payload does not name its call kind; both call steps are on the overview.";

function callSurface(facts: NotificationFacts): string | null {
  if (facts.callKind === "marketing") return "marketing_call";
  if (facts.callKind === "onboarding") return "onboarding_call";
  return sectionSurface(facts);
}

// ---------------------------------------------------------------------------
// The registry. One entry per notifications.type the product queues.
// ---------------------------------------------------------------------------

export const NOTIFICATION_TARGETS: Readonly<Record<string, TargetRule>> = {
  // --- never in a bell: the link IS the message ---
  welcome_magic_link: { excludedFromInbox: true },
  return_magic_link: { excludedFromInbox: true },
  team_invite: { excludedFromInbox: true },

  // --- the client's journey moves ---
  section_unlocked: {
    client: sectionSurface,
    overviewReason: "A payload that named no section we serve; the overview lists every step it could have been."
  },
  questionnaire_nudge: {
    client: (facts) => sectionSurface(facts) ?? null,
    overviewReason: "Legacy nudges predate the section payload; the overview shows which questionnaire is open."
  },
  client_step_confirmation_nudge: {
    client: (facts) => (facts.confirmStep === "apple" ? "app:apple" : facts.confirmStep === "profile" ? "profile" : null),
    overviewReason: "A nudge whose payload names no step; the confirmation button rides the bell row itself."
  },
  call_reminder_day: { client: callSurface, overviewReason: CALL_REASON },
  call_reminder_3h: { client: callSurface, overviewReason: CALL_REASON },
  call_reminder_15m: { client: callSurface, overviewReason: CALL_REASON },
  call_reminder_live: { client: callSurface, overviewReason: CALL_REASON },
  call_reminder_1h: { client: callSurface, overviewReason: CALL_REASON },
  call_no_show: { client: callSurface, overviewReason: CALL_REASON },

  // --- know-how ---
  knowhow_shared: { client: fixed("knowhow") },
  knowhow_folder_shared: { client: fixed("knowhow") },
  knowhow_resources_ready: { client: fixed("knowhow") },

  // --- materials ---
  deliverable_review: {
    client: deliverableClientSurface,
    staff: deliverableStaffStep,
    overviewReason: "A pre-F131 row with no kind on the payload and no audit row left to read it from."
  },
  deliverable_feedback_resolved: {
    client: deliverableClientSurface,
    staff: deliverableStaffStep,
    overviewReason: "As deliverable_review: an unnamed material cannot name its step."
  },
  deliverable_partner_review: {
    staff: deliverableStaffStep,
    overviewReason: "A build that reported no kind; the client's console lists every material."
  },
  deliverable_client_note: { staff: deliverableStaffStep, overviewReason: "Same as deliverable_partner_review." },
  // F162: a free-text message from the client is about no single step; the
  // console opens on the client and the partner reads it in the mail itself.
  client_partner_message: { staff: () => null, overviewReason: "A message from the client names no step; the console opens on the client." },
  deliverable_build_failed: { staff: deliverableStaffStep, overviewReason: "Same as deliverable_partner_review." },
  deliverable_edit_failed: { staff: deliverableStaffStep, overviewReason: "Same as deliverable_partner_review." },
  gate_approved: { staff: deliverableStaffStep, overviewReason: "Same as deliverable_partner_review." },
  order_links_integrated: { client: fixed("salespage") },
  staff_override: {
    client: (facts) => {
      const kind = facts.overrideKind;
      return kind ? OVERRIDE_CLIENT_SURFACE[kind] ?? null : null;
    },
    overviewReason: "A legacy override row queued before the kind rode the payload."
  },

  // --- the bot changes hands ---
  bot_transfer_ready: { client: fixed("beta") },
  bot_transfer_done: { client: fixed("beta") },
  bot_transfer_client_fix: { client: fixed("beta") },
  bot_transfer_staff_failed: { staff: fixedStep("beta") },

  // --- the handover ---
  handover_owner_role: { client: fixed("handover") },
  owner_role_client_fix: { client: fixed("handover") },
  owner_role_staff_failed: { staff: fixedStep("handover") },

  // --- the partner ---
  // F139: the client's own dashboard is where the partner card now names
  // somebody, so the overview IS the thing the message is about.
  partner_assigned: { client: fixed("home") },
  partner_assigned_staff: {
    staff: () => null,
    overviewReason: "A new client has no step of their own yet - the assignment is about the WHOLE client, so the console opens them, not a step."
  },

  // --- the profile ---
  profile_map_applied: { client: fixed("profile") },

  // --- training ---
  training_started: { staff: fixedStep("training") },
  training_failed: { staff: fixedStep("training") },
  training_result: { staff: fixedStep("training") },

  // --- questionnaires and the app ---
  dfy_q2_ready: { staff: fixedStep("q2") },
  // F157: the import's summary is about the WHOLE client - four materials, a
  // questionnaire and the approvals at once - so no single step is its subject.
  sales_only_import_summary: {
    staff: () => null,
    overviewReason: "An import summary spans every step it touched; the client's console is the one place that shows all of them."
  },
  app_wizard_submitted: { staff: fixedStep("app_questionnaire") },
  // F136c: the prefill landed, the client reads and corrects the questionnaire.
  app_prefill_ready: { client: fixed("app:questionnaire") },

  // --- staff, no step of their own ---
  action_required: {
    staff: sectionStaffStep,
    overviewReason: "A transition that named no section; the client's console is the signpost."
  },
  transcript_failed: {
    staff: () => null,
    overviewReason: "A recording belongs to whichever step it was dictated on, and the payload does not carry it. Naming a step here would be a guess."
  },
  instance_team: {
    staff: fixedStep("training"),
    overviewReason: "The team join belongs to the trained instance."
  },

  // --- messages and probes ---
  partner_message: {
    client: () => null,
    overviewReason: "The partner writes about whatever they like; the message has no step of its own."
  },
  system_test: {
    client: () => null,
    overviewReason: "A probe of the channel, not a fact about the journey."
  },
  admin_delivery_test: {
    staff: () => null,
    overviewReason: "A probe of the channel, about no client at all."
  }
};

/** Every type the registry knows. The gate iterates this against the catalog. */
export const NOTIFICATION_TARGET_TYPES: readonly string[] = Object.keys(NOTIFICATION_TARGETS);

export function notificationTargetRule(type: string): TargetRule | null {
  return NOTIFICATION_TARGETS[type] ?? null;
}

/**
 * The client surface this notification leads to, or null when the honest answer
 * is the overview. Null is only ever returned for a type whose rule carries a
 * written `overviewReason`.
 */
export function clientNotificationSurface(
  type: string,
  facts: NotificationFacts = {}
): ClientNotificationSurface | null {
  const resolved = NOTIFICATION_TARGETS[type]?.client?.(facts) ?? null;
  return resolved && isClientNotificationSurface(resolved) ? resolved : null;
}

/** The staff step this notification leads to, or null for the client overview. */
export function staffNotificationStep(
  type: string,
  facts: NotificationFacts = {}
): StaffNotificationStep | null {
  const resolved = NOTIFICATION_TARGETS[type]?.staff?.(facts) ?? null;
  return resolved && isStaffNotificationStep(resolved) ? resolved : null;
}

/**
 * `/` is the overview: every step with its state, never a recommendation.
 *
 * 🔴 The surface is written RAW, never percent-encoded. It comes from the closed
 * list above (lowercase, `_` and `:`), a colon is legal in a query value, and
 * `clientDeepLink` has always written `/?open=app:apple`. Encoding it produces
 * `app%3Aapple` - the same link in a second spelling, which every string
 * comparison in the product then reads as a different one.
 */
export function clientNotificationUrl(type: string, facts: NotificationFacts = {}): string {
  const surface = clientNotificationSurface(type, facts);
  return surface && surface !== "home" ? `/?open=${surface}` : "/";
}

export function staffNotificationUrl(
  type: string,
  clientId: string | null,
  facts: NotificationFacts = {}
): string {
  if (!clientId) return "/";
  const step = staffNotificationStep(type, facts);
  const base = `/?client=${encodeURIComponent(clientId)}`;
  return step ? `${base}&staffStep=${encodeURIComponent(step)}` : base;
}

export function notificationExcludedFromInbox(type: string): boolean {
  return NOTIFICATION_TARGETS[type]?.excludedFromInbox === true;
}
